import { useQuery } from "@apollo/client";
import { ContaminationDeclarationForEvent } from "../graphql/queries/contaminationDeclarationForEvent";
import { getPrintReadiness, PrintReadiness } from "../utils/contaminationDeclaration/gating";

export interface DeclarationForEvent {
    id: string;
    declarationAccepted?: boolean | null;
    updatedAt?: string | null;
    validity?: { isValid: boolean; missingQuestionIndexList?: string[] | null } | null;
}

/**
 * Looks up the declaration of one ASSET_REMOVE event. `declaration === null`
 * with no error means none has been started; an error (typically no node
 * access) is reported so callers can stay silent rather than nudge.
 *
 * `available` is the second half of the same round trip: whether a declaration
 * may be *started* for this event at all. It gates the create affordance only
 * — an existing declaration stays readable, editable and printable wherever
 * its owner sits, so never gate `declaration` on it.
 *
 * It defaults to `false` while loading and on error, which is the safe way
 * round: an unanswered question must not offer a button the server refuses.
 */
export const useDeclarationForEvent = (installedBaseEventId: string | null | undefined, skip = false) => {
    const { data, loading, error } = useQuery(ContaminationDeclarationForEvent, {
        variables: { installedBaseEventId },
        skip: skip || !installedBaseEventId,
    });
    const declaration: DeclarationForEvent | null = data?.contaminationDeclarationForEvent ?? null;
    const available: boolean = data?.contaminationDeclarationAvailability === true;
    const readiness: PrintReadiness | null = declaration
        ? getPrintReadiness(declaration.validity, declaration.declarationAccepted === true)
        : null;
    return { declaration, readiness, available, loading, error };
};
